import React, { FC, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DotLottleLoader } from '../Loader';
import { useAttributeCatalog } from './useAttributeCatalog';

interface Props {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

// Grouped attribute-option picker: one accordion per attribute group, each value
// a checkbox. Shared by the entity edit forms (product category / sub-profession /
// area of focus). Mirrors the attributes admin accordion layout.
export const AttributeOptionsSelector: FC<Props> = ({ selectedIds, onChange }) => {
  const { attributes, loading } = useAttributeCatalog();
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = (valueId: number, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(valueId);
    else next.delete(valueId);
    onChange([...next]);
  };

  if (loading) {
    return (
      <Box className="flex justify-center py-6">
        <DotLottleLoader sx={{ width: 48, height: 48 }} fill={false} />
      </Box>
    );
  }

  if (attributes.length === 0) {
    return (
      <Typography sx={{ color: 'var(--text-2)', fontSize: 13, py: 2 }}>
        No attribute options found.
      </Typography>
    );
  }

  return (
    <Box>
      {attributes.map((attr) => {
        const values = attr.values || [];
        const count = values.reduce((n, v) => (selected.has(v.id) ? n + 1 : n), 0);
        return (
          <Accordion key={attr.id} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box className="flex items-center gap-2">
                <Typography fontWeight={600}>{attr.name}</Typography>
                {count > 0 && <Chip size="small" label={`${count} selected`} />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                {values.map((v) => (
                  <FormControlLabel
                    key={v.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.has(v.id)}
                        onChange={(e) => toggle(v.id, e.target.checked)}
                      />
                    }
                    label={v.name}
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
